import { useCallback, useState } from 'react';
import type { SyntheticEvent } from 'react';

import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoIcon from '@mui/icons-material/Info';
import VrpanoIcon from '@mui/icons-material/Vrpano';
import InsertPageBreakIcon from '@mui/icons-material/InsertPageBreak';
import VisibilityIcon from '@mui/icons-material/Visibility';
import Avatar from '@mui/material/Avatar';
import Stack from '@mui/material/Stack';
import { useMediaQuery, useTheme } from '@mui/material';

import { OrientationEnum, PaperFormatEnum } from '@e-socna/data-layer';
import { EMPTY_ARRAY, EMPTY_OBJECT, EMPTY_STRING } from '@e-socna/empty';
import type { ContentOriginId } from '@e-socna/data-layer';

import { stopPropagation } from '../../../../utils/stop-propagation.util';
import { useReferencesContext } from '../../../../contexts/ReferencesContext/hooks/useReferencesContext.hook';

import { EllipsisTooltip } from '../../../EllipsisTooltip';
import { PageViewer } from '../../../PageViewer';

import { pathToChapters } from '../../utils/path-to-chapters.util';
import { displayConditionLabelOf } from '../../utils/display-condition-choice.util';
import { FoldingMode } from '../../enums/folding-mode.enum';
import { useDocumentEditorContext } from '../../contexts/DocumentEditorContext/hooks/useDocumentEditorContext.hook';
import type { EnrichedSection } from '../../types/enriched-section.type';
import type { Editable } from '../../types/editable.type';

import { useContentEditGate } from '../../hooks/useContentEditGate.hook';

import { SectionMenu } from './SectionMenu';
import { ContentOrigin } from './ContentOrigin';
import { SectionDetailsDialog } from './SectionDetailsDialog';
import { LoadingSection } from './LoadingSection';

interface Props {
  readonly section: EnrichedSection;
}

export const displaySection = (section: Readonly<EnrichedSection>) =>
  section.subSections.map((subSection) => (
    <Section key={subSection._id} section={subSection} />
  ));

const PAGEBREAK_STYLE = '5px dashed white';

export const Section = ({ section }: Readonly<Props>) => {
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.up('sm'));

  const { actions } = useDocumentEditorContext<Editable>();
  const { isAllowed: isContentEditable } = useContentEditGate();

  const [isOpenSectionDetailsDialog, setIsOpenSectionDetailsDialog] =
    useState(false);

  const handleSectionDetailsDialogClose = useCallback(
    (event: Readonly<SyntheticEvent>) => {
      stopPropagation(event);
      setIsOpenSectionDetailsDialog(false);
    },
    [],
  );

  const handleInfoButtonClick = useCallback(
    (event: Readonly<SyntheticEvent>) => {
      stopPropagation(event);
      setIsOpenSectionDetailsDialog(true);
    },
    [],
  );

  const handleAccordionChange = useCallback(
    (_event: Readonly<SyntheticEvent>, expanded: boolean) => {
      actions.setFoldingMode(
        section._id,
        expanded ? FoldingMode.Unfolded : FoldingMode.Folded,
      );
    },
    [actions, section._id],
  );

  const { resolversIdMap } = useReferencesContext();

  if (section.isLoading) {
    return <LoadingSection id={section._id} key={section._id} />;
  }

  const chapterNumber = pathToChapters(section.displayPath ?? EMPTY_ARRAY);
  const sectionTitle = section.title ?? EMPTY_STRING;
  const tooltipTitle = section.isVirtual
    ? section.title
    : `${chapterNumber} ${sectionTitle}`;

  return (
    <Accordion
      disableGutters
      elevation={0}
      expanded={!section.isFolded}
      onChange={handleAccordionChange}
      slotProps={{ transition: { unmountOnExit: true, timeout: 0 } }} // performance
      sx={{
        width: '100%',
        '&:before': {
          display: 'none',
        },
        border: '4px solid',
        borderColor: (theme) =>
          theme.palette.grey[section.isVirtual ? 400 : 200],
      }}
    >
      <AccordionSummary
        aria-controls={`accordion-${section._id}-content`}
        aria-label="Déplier"
        expandIcon={<ExpandMoreIcon />}
        id={`accordion-${section._id}-header`}
        sx={{
          ...(section.hasPagebreakBefore
            ? { borderTop: PAGEBREAK_STYLE }
            : EMPTY_OBJECT),
          ...(section.hasPagebreakAfter && section.isFolded
            ? { borderBottom: PAGEBREAK_STYLE }
            : EMPTY_OBJECT),
          backgroundColor: (theme) =>
            theme.palette.grey[section.isVirtual ? 400 : 200],
          color: (theme) =>
            theme.palette.getContrastText(theme.palette.grey[200]),
          '& :first-of-type': {
            minWidth: 0,
          },
          ...(!matches
            ? {
                pl: '4px',
                pr: '8px',
              }
            : EMPTY_OBJECT),
        }}
      >
        {isContentEditable ? <SectionMenu section={section} /> : null}
        <EllipsisTooltip title={tooltipTitle}>
          <Typography
            sx={{
              ml: matches ? 1 + 2 * (section.displayPath?.length ?? 0) : 1,
              lineHeight: 2.5,
              flexGrow: 1,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              mr: 1,
              fontStyle: section.isVirtual ? 'italic' : 'normal',
            }}
          >
            {section.isVirtual ? (
              section.title
            ) : (
              <>
                <Box component="span" sx={{ mr: matches ? 3 : 1 }}>
                  {chapterNumber}
                </Box>
                {sectionTitle}
              </>
            )}
          </Typography>
        </EllipsisTooltip>
        {matches ? (
          <Stack
            direction="row"
            spacing={2}
            sx={{
              marginRight: '16px',
              minHeight: '100%',
              lineHeight: 2.5,
              alignItems: 'center',
              justifyContent: 'flex-end',
            }}
          >
            {section.orientation === OrientationEnum.Landscape ? (
              <Tooltip title="Orientation Paysage">
                <Avatar>
                  <VrpanoIcon />
                </Avatar>
              </Tooltip>
            ) : null}
            {section.paperFormat === PaperFormatEnum.A3 ? (
              <Tooltip title="Format A3">
                <Avatar>A3</Avatar>
              </Tooltip>
            ) : null}
            {section.displayCondition ? (
              <Tooltip
                title={`Section conditionnelle : ${displayConditionLabelOf(
                  section.displayCondition,
                  resolversIdMap,
                )}`}
              >
                <Avatar>
                  <VisibilityIcon />
                </Avatar>
              </Tooltip>
            ) : null}
            {section.hasPagebreakAfter || section.hasPagebreakBefore ? (
              <Tooltip title="Saut de page">
                <Avatar>
                  <InsertPageBreakIcon />
                </Avatar>
              </Tooltip>
            ) : null}
            <ContentOrigin section={section} />
          </Stack>
        ) : (
          <>
            <Tooltip title="Obtenir des informations sur cette section">
              <IconButton
                aria-label="Obtenir des informations sur cette section"
                component="span"
                onClick={handleInfoButtonClick}
                role="button"
                tabIndex={0}
              >
                <InfoIcon />
              </IconButton>
            </Tooltip>
            <SectionDetailsDialog
              contentOriginId={section.contentOriginId as ContentOriginId}
              hasPagebreakAfter={section.hasPagebreakAfter}
              hasPagebreakBefore={section.hasPagebreakBefore}
              isOpen={isOpenSectionDetailsDialog}
              onClose={handleSectionDetailsDialogClose}
              orientation={section.orientation}
              paperFormat={section.paperFormat}
            />
          </>
        )}
      </AccordionSummary>
      <AccordionDetails
        sx={{
          padding: 0,
          background: (theme) => theme.palette.grey[200],
          ...(section.hasPagebreakAfter && !section.isFolded
            ? { borderBottom: PAGEBREAK_STYLE }
            : EMPTY_OBJECT),
        }}
      >
        {!!section.content && (
          <PageViewer
            html={section.content}
            orientation={section.orientation}
            paperFormat={section.paperFormat}
          />
        )}
        {!!section.subSections.length && displaySection(section)}
      </AccordionDetails>
    </Accordion>
  );
};
